import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, FileCheck2, Server, Activity, AlertTriangle, BookOpen, CheckCircle } from 'lucide-react';
import { SEO } from '../components/seo/SEO';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../context/LanguageContext';
import styles from './TrustPage.module.css';

const pillars = [
  {
    icon: Shield,
    title: 'Security & Access',
    bullets: ['IAM with MFA/Conditional Access', 'Least Privilege, Roles & Permissions', 'Secrets and key management'],
  },
  {
    icon: FileCheck2,
    title: 'Compliance & Privacy',
    bullets: ['GDPR/DPA/AVV ready', 'Privacy policies, retention/deletion', 'Vendor risk & data processing agreements'],
  },
  {
    icon: Lock,
    title: 'Data Protection',
    bullets: ['PII filtering, masking, scopes', 'Audit trails, access matrix', 'Backups & restore tests'],
  },
  {
    icon: Activity,
    title: 'AI Governance',
    bullets: ['EU AI Act aware', 'Human-in-the-loop & guardrails', 'Logging, monitoring, drift/quality checks'],
  },
  {
    icon: Server,
    title: 'Reliability & Ops',
    bullets: ['Staging/production discipline', 'Rollback strategies', 'SLOs, observability, incident runbooks'],
  },
  {
    icon: AlertTriangle,
    title: 'Risk Management',
    bullets: ['Threat modeling & playbooks', 'Tabletop exercises', 'Escalation & communications plans'],
  },
];

const pillarsDE = [
  {
    icon: Shield,
    title: 'Security & Access',
    bullets: ['IAM mit MFA/Conditional Access', 'Least Privilege, Rollen & Freigaben', 'Secrets- und Key-Management'],
  },
  {
    icon: FileCheck2,
    title: 'Compliance & Privacy',
    bullets: ['GDPR/DPA/AVV bereit', 'Datenschutzkonzepte, Aufbewahrung/Löschung', 'Vendor Risk & AV-Verträge'],
  },
  {
    icon: Lock,
    title: 'Data Protection',
    bullets: ['PII-Filter, Masking, Scopes', 'Audit Trails, Zugriffsmatrix', 'Backups & Restore-Tests'],
  },
  {
    icon: Activity,
    title: 'AI Governance',
    bullets: ['EU AI Act bewusst', 'Human-in-the-loop & Guardrails', 'Logging, Monitoring, Drift/Quality Checks'],
  },
  {
    icon: Server,
    title: 'Reliability & Ops',
    bullets: ['Staging/PRD Disziplin', 'Rollback-Strategien', 'SLOs, Observability, Incident-Runbooks'],
  },
  {
    icon: AlertTriangle,
    title: 'Risk Management',
    bullets: ['Threat Modeling & Playbooks', 'Tabletop-Übungen', 'Escalation & Comms Plans'],
  },
];

const artifacts = [
  'Security Overview (IAM, Network, Data, Logging)',
  'DPA/AVV & Subprocessor List',
  'Architecture & Data Flows',
  'Runbooks (Incident, Backup/Restore, Rollback)',
  'Testing & Quality Gates (Unit, E2E, Security)',
  'Change & Release Policy',
];

const artifactsDE = [
  'Security-Übersicht (IAM, Netzwerk, Daten, Logging)',
  'DPA/AVV & Subprocessor-Liste',
  'Architektur & Datenflüsse',
  'Runbooks (Incident, Backup/Restore, Rollback)',
  'Testing & Quality Gates (Unit, E2E, Security)',
  'Change & Release Policy',
];

const evidenceVault = [
  {
    label: 'DPA/AVV',
    title: 'Data Protection & Processing Agreements',
    image: '/images/strategic_roadmap.png',
    alt: 'Sanitized preview of a DPA template',
  },
  {
    label: 'Security Overview',
    title: 'IAM, logging & network',
    image: '/images/security_governance_hero.png',
    alt: 'Sanitized preview of a security architecture',
  },
  {
    label: 'Incident Runbook',
    title: 'Response & Reporting',
    image: '/images/industries_global.png',
    alt: 'Sanitized preview of an incident runbook',
  },
];

export const TrustPage: React.FC = () => {
  const { language } = useLanguage();

  const copy = language === 'de' ? {
    seoDescription: 'Transparente Security-, Privacy- und AI-Governance-Standards für Enterprise-Kunden. EU-ready, auditierbar, beschaffungssicher.',
    faq: [
      { q: 'Welche Security-Artefakte stellen wir bereit?', a: 'DPA/AVV, Subprocessor-Liste, Security-Übersicht, Backup/Restore- und DR-Summary, Change/Release-Policy, Logging/Audit-Übersicht.' },
      { q: 'Sind wir EU AI Act und NIS2 bewusst?', a: 'Ja. Wir klassifizieren Use Cases, setzen Guardrails, Logging und Audit Trails um und unterstützen Security/Compliance-Reviews.' },
      { q: 'Wie schnell liefern wir einen Pilot?', a: 'In 8–12 Wochen mit Guardrails, KPIs, Observability und Runbook. Discovery dauert typischerweise 1–2 Wochen.' },
    ],
    hero: {
      eyebrow: 'Trust & Compliance',
      title: 'Auditierbar. Sicher. Betriebsbereit.',
      subtitle: 'Wir integrieren Security, Privacy und AI-Governance in jede Phase. NIS2/GDPR ready, EU AI Act bewusst – mit klaren Artefakten für Procurement und Security.',
      ctaPrimary: 'Security-Call planen',
      ctaSecondary: 'Security-Pack anfordern',
    },
    sectionHeader: {
      title: 'Wesentliche Kontrollen',
      subtitle: 'Security- und Compliance-Bausteine, die wir standardmäßig adressieren.',
    },
    artifacts: {
      eyebrow: 'Procurement-ready',
      title: 'Artefakte für Security & Einkauf',
      desc: 'Wir liefern die Unterlagen, die Ihr Security-Review beschleunigen.',
      evidenceBadge: 'Verifiziert für Procurement',
      evidenceOverlay: 'Vertraulich / Vorschau unscharf',
      requestPack: 'Security Pack anfordern',
      email: 'Direkt per E-Mail',
      badgeText: 'DPA/AVV, Subprocessor-Liste, Security-Übersicht, Logging-/Backup-Konzept, Change/Release-Policy.',
      badgeCta: 'Anfordern',
    },
    callout: {
      eyebrow: 'Nächster Schritt',
      title: 'Security- & Compliance-Review mit uns planen',
      text: 'Wir zeigen Guardrails, Logging, AI-Governance und liefern Ihnen ein prüfbares Set an Artefakten.',
      cta: 'Security-Call buchen',
    }
  } : {
    seoDescription: 'Transparent security, privacy, and AI governance standards for enterprise customers. EU-ready, auditable, procurement-friendly.',
    faq: [
      { q: 'Which security artifacts do we provide?', a: 'DPA/AVV, subprocessor list, security overview, backup/restore and DR summary, change/release policy, logging/audit overview.' },
      { q: 'Are we aware of the EU AI Act and NIS2?', a: 'Yes. We classify use cases, implement guardrails, logging and audit trails, and support security/compliance reviews.' },
      { q: 'How quickly do we deliver a pilot?', a: 'In 8–12 weeks with guardrails, KPIs, observability and a runbook. Discovery typically takes 1–2 weeks.' },
    ],
    hero: {
      eyebrow: 'Trust & Compliance',
      title: 'Auditable. Secure. Production-ready.',
      subtitle: 'We integrate security, privacy and AI governance into every phase. NIS2/GDPR ready, EU AI Act aware — with clear artifacts for procurement and security.',
      ctaPrimary: 'Schedule security call',
      ctaSecondary: 'Request security pack',
    },
    sectionHeader: {
      title: 'Core Controls',
      subtitle: 'Security and compliance building blocks we address by default.',
    },
    artifacts: {
      eyebrow: 'Procurement-ready',
      title: 'Artifacts for Security & Procurement',
      desc: 'We deliver the documentation that accelerates your security review.',
      evidenceBadge: 'Verified for procurement',
      evidenceOverlay: 'Confidential / Preview blurred',
      requestPack: 'Request Security Pack',
      email: 'Email us directly',
      badgeText: 'DPA/AVV, subprocessor list, security overview, logging/backup concept, change/release policy.',
      badgeCta: 'Request',
    },
    callout: {
      eyebrow: 'Next step',
      title: 'Plan a security & compliance review with us',
      text: 'We demonstrate guardrails, logging, AI governance and deliver a verifiable set of artifacts.',
      cta: 'Book security call',
    }
  };

  return (
    <div className={styles.page}>
      <SEO
        title="Trust, Security & Compliance"
        description={copy.seoDescription}
        canonical="/trust"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: copy.faq[0].q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: copy.faq[0].a,
              },
            },
            {
              '@type': 'Question',
              name: copy.faq[1].q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: copy.faq[1].a,
              },
            },
            {
              '@type': 'Question',
              name: copy.faq[2].q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: copy.faq[2].a,
              },
            },
          ],
        }}
      />

      <section className={styles.hero}>
        <div className={styles.hero__container}>
          <motion.span
            className={styles.hero__eyebrow}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            {copy.hero.eyebrow}
          </motion.span>
          <motion.h1
            className={styles.hero__title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {copy.hero.title}
          </motion.h1>
          <motion.p
            className={styles.hero__subtitle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
          >
            {copy.hero.subtitle}
          </motion.p>
          <motion.div
            className={styles.hero__ctas}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <Button variant="primary" size="lg" to="/consultation">{copy.hero.ctaPrimary}</Button>
            <Button variant="secondary" size="lg" to="/contact">{copy.hero.ctaSecondary}</Button>
          </motion.div>
        </div>
      </section>

      <section className={styles.pillars}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>{copy.sectionHeader.title}</h2>
            <p>{copy.sectionHeader.subtitle}</p>
          </div>
          <div className={styles.grid}>
            {(language === 'de' ? pillarsDE : pillars).map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className={styles.card}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.15, delay: idx * 0.05 }}
                >
                  <div className={styles.card__icon}>
                    <Icon size={22} />
                  </div>
                  <h3>{item.title}</h3>
                  <ul>
                    {item.bullets.map((b) => (
                      <li key={b}>
                        <span className={styles.dot} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className={styles.artifacts}>
        <div className={styles.container}>
          <div className={styles.artifacts__content}>
            <div className={styles.artifacts__text}>
              <p className={styles.eyebrow}>{copy.artifacts.eyebrow}</p>
              <h2>{copy.artifacts.title}</h2>
              <p>{copy.artifacts.desc}</p>
              <ul className={styles.artifacts__list}>
                {(language === 'de' ? artifactsDE : artifacts).map((a) => (
                  <li key={a}>
                    <CheckCircle size={18} />
                    {a}
                  </li>
                ))}
              </ul>
              <div className={styles.evidence}>
                <p className={styles.eyebrow}>Evidence Vault</p>
                <h3>{language === 'de' ? 'Sanitisierte Artefakt-Vorschau' : 'Sanitized artifact previews'}</h3>
                <div className={styles.evidence__badge}>
                  <span className={styles.evidence__badgeDot} />
                  {copy.artifacts.evidenceBadge}
                </div>
                <div className={styles.evidence__grid}>
                  {evidenceVault.map((item) => (
                    <div key={item.label} className={styles.evidence__card}>
                      <div className={styles.evidence__preview}>
                        <picture>
                          <source
                            type="image/webp"
                            srcSet={`${item.image.replace(/\.(png|jpg|jpeg)$/i, '')}-800.webp 800w, ${item.image.replace(/\.(png|jpg|jpeg)$/i, '')}-1600.webp 1600w`}
                            sizes="(max-width: 768px) 100vw, 240px"
                          />
                          <img
                            src={item.image}
                            alt={item.alt}
                            className={styles.evidence__previewImage}
                            loading="lazy"
                          />
                        </picture>
                        <div className={styles.evidence__previewOverlay}>
                          <span className={styles.evidence__label}>{copy.artifacts.evidenceOverlay}</span>
                        </div>
                      </div>
                      <div className={styles.evidence__meta}>
                        <span className={styles.evidence__title}>{item.title}</span>
                        <span className={styles.evidence__subtitle}>{item.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.evidence__imageWrapper}>
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/images/team_collaboration-800.webp 800w, /images/team_collaboration-1600.webp 1600w"
                    sizes="(max-width: 900px) 100vw, 520px"
                  />
                  <img
                    src="/images/team_collaboration.png"
                    alt="Security Team Collaboration"
                    className={styles.evidence__image}
                    loading="lazy"
                  />
                </picture>
                <div className={styles.evidence__overlay} />
              </div>
              <div className={styles.artifacts__actions}>
                <Button variant="secondary" size="md" to="/contact">{copy.artifacts.requestPack}</Button>
                <Button variant="ghost" size="md" to="mailto:info@integrovaai.de">{copy.artifacts.email}</Button>
              </div>
            </div>
            <div className={styles.artifacts__badge}>
              <div className={styles.badge__icon}>
                <BookOpen size={24} />
              </div>
              <picture>
                <source
                  type="image/webp"
                  srcSet="/images/enterprise-hero-800.webp 800w, /images/enterprise-hero-1600.webp 1600w"
                  sizes="(max-width: 900px) 100vw, 320px"
                />
                <img
                  className={styles.artifacts__photo}
                  src="/images/enterprise-hero.jpg"
                  alt="Enterprise-grade infrastructure and governance"
                  loading="lazy"
                />
              </picture>
              <h3>Security Pack</h3>
              <p>{copy.artifacts.badgeText}</p>
              <Button variant="primary" size="md" to="/contact">{copy.artifacts.badgeCta}</Button>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.callout}>
        <div className={styles.container}>
          <div className={styles.callout__content}>
            <div>
              <p className={styles.callout__eyebrow}>{copy.callout.eyebrow}</p>
              <h3 className={styles.callout__title}>{copy.callout.title}</h3>
              <p className={styles.callout__text}>{copy.callout.text}</p>
            </div>
            <Button variant="primary" size="lg" to="/consultation">{copy.callout.cta}</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrustPage;
